import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  tableCellClasses,
  styled,
  Button,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import api from '../api';
import { formatErrorMessage } from '../utils';
import PrintIcon from '@mui/icons-material/Print';

interface Sale {
  id: number;
  company: {
    id: number;
    name: string;
  };
  site: {
    id: number;
    name: string;
  };
  items: {
    id?: number;
    item: string;
    amount: string | number;
    state?: string;
    release_date?: string | null;
    start_date?: string | null;
    end_date?: string | null;
  }[];
}

interface GroupedSale {
  id: number;
  company_name: string;
  site_name: string;
  content: string;
  schedule: string;
  quantity: number;
  release_date?: string;
  state?: string;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    fontSize: 12,
    whiteSpace: 'nowrap',
    textAlign: 'center',
    padding: '6px 3px',
    border: '1px solid #fff',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    whiteSpace: 'nowrap',
    padding: '6px 3px',
    textAlign: 'center',
    border: '1px solid rgba(224, 224, 224, 1)',
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#fff',
  },
  '&:nth-of-type(even)': {
    backgroundColor: '#fafafa',
  },
}));

const ProductionDocument: React.FC = () => {
  const [items, setItems] = useState<GroupedSale[]>([]);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const month = String(currentDate.getMonth() + 1).padStart(2, '0');
  const day = String(currentDate.getDate()).padStart(2, '0');

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
  setLoading(true);
  try {
    const response = await api.get('/api/sales');
    const sales: Sale[] = response.data;

    const groupedSales = sales.flatMap(sale =>
      sale.items
        .filter(item => {
          const itemName = item.item?.replace(/\s/g, '').toLowerCase() || '';
          const isExcluded =
            itemName.includes('구조검토') ||
            itemName.includes('해체') ||
            itemName.includes('설치') ||
            itemName.includes('이전');

          if (isExcluded) console.log('❌ 제외됨:', itemName);
          else console.log('✅ 포함됨:', itemName);

          return !isExcluded;
        })
        .map(item => ({
          key: `${sale.company.name}-${sale.site.name}-${item.item}`,
          value: {
            id: sale.id,
            company_name: sale.company.name,
            site_name: sale.site.name,
            content: item.item,
            schedule: item.start_date && item.end_date ? `${item.start_date}~${item.end_date}` : '',
            quantity: 1,
            release_date: item.release_date || '',
            state: item.state || '',
          },
        }))
    ).reduce<{ [key: string]: GroupedSale }>((acc, { key, value }) => {
      if (!acc[key]) {
        acc[key] = value;
      } else {
        acc[key].quantity += 1;
      }
      return acc;
    }, {});

    // 출고예정일 필터링 + 정렬
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);

    const finalList = Object.values(groupedSales)
      .filter(item => {
        if (!item.release_date) return true;
        const releaseDate = new Date(item.release_date);
        return releaseDate >= sevenDaysAgo; // 7일 전보다 이후인 것만 남김
      })
      .sort((a, b) => {
        const aDate = a.release_date ? new Date(a.release_date) : new Date('2100-01-01');
        const bDate = b.release_date ? new Date(b.release_date) : new Date('2100-01-01');
        return aDate.getTime() - bDate.getTime(); // 빠른 순 정렬
      });

    setItems(finalList);
  } catch (error) {
    const errorMessage = formatErrorMessage(error);
    enqueueSnackbar(errorMessage, { variant: 'error' });
  } finally {
    setLoading(false);
  }
};

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>생산 계획</title>
            <style>
              @page {
                size: A4 landscape;
                margin: 20mm;
              }
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 20px;
              }
              .header {
                margin-bottom: 20px;
              }
              .title {
                font-size: 18px;
                font-weight: bold;
                text-align: center;
                margin-bottom: 10px;
              }
              .date {
                font-size: 12px;
                text-align: right;
                margin-bottom: 20px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                font-size: 12px;
              }
              th, td {
                border: 1px solid #000;
              
              
              
                padding: 6px 3px;
                text-align: center;
              }
              th {
                background-color: #000;
                color: #fff;
              }
              tr:nth-child(even) {
                background-color: #f9f9f9;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <div class="title">${year}년 ${month}월 생산 계획</div>
              <div class="date">작성일 ${year}.${month}.${day}</div>
            </div>
            <table>
              <thead>
                <tr>
                  <th style="width: 40px">번호</th>
                  <th style="width: 60px">업체명</th>
                  <th style="width: 150px">현장명</th>
                  <th style="width: 150px">품명</th>
                  <th style="width: 120px">일정</th>
                  <th style="width: 120px">규격</th>
                  <th style="width: 40px">수량</th>
                  <th style="width: 60px">비고</th>
                  <th style="width: 80px">출고예정</th>
                  <th style="width: 80px">상태</th>
                </tr>
              </thead>
              <tbody>
                ${items.map((item, index) => `
                  <tr>
                    <td>${index + 1}</td>
                    <td>${item.company_name}</td>
                    <td>${item.site_name}</td>
                    <td>${item.content}</td>
                    <td>${item.schedule}</td>
                    <td></td>
                    <td>${item.quantity}</td>
                    <td></td>
                    <td>${item.release_date || ''}</td>
                    <td>${item.state || '-'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    }
  };

  return (
    <Box sx={{ width: '100%', overflow: 'hidden', position: 'relative' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
          {year}년 {month}월 생산 계획
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ mr: 2 }}>
            작성일 {year}.{month}.{day}
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            프린트
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden', position: 'relative' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <StyledTableCell sx={{ width: '40px' }}>번호</StyledTableCell>
                  <StyledTableCell sx={{ width: '60px' }}>업체명</StyledTableCell>
                  <StyledTableCell sx={{ width: '150px' }}>현장명</StyledTableCell>
                  <StyledTableCell sx={{ width: '150px' }}>품명</StyledTableCell>
                  <StyledTableCell sx={{ width: '120px' }}>일정</StyledTableCell>
                  <StyledTableCell sx={{ width: '120px' }}>규격</StyledTableCell>
                  <StyledTableCell sx={{ width: '40px' }}>수량</StyledTableCell>
                  <StyledTableCell sx={{ width: '60px' }}>비고</StyledTableCell>
                  <StyledTableCell sx={{ width: '80px' }}>출고예정</StyledTableCell>
                  <StyledTableCell sx={{ width: '80px' }}>상태</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => (
                  <StyledTableRow key={item.id}>
                    <StyledTableCell>{index + 1}</StyledTableCell>
                    <StyledTableCell>{item.company_name}</StyledTableCell>
                    <StyledTableCell>{item.site_name}</StyledTableCell>
                    <StyledTableCell>{item.content}</StyledTableCell>
                    <StyledTableCell>{item.schedule}</StyledTableCell>
                    <StyledTableCell></StyledTableCell>
                    <StyledTableCell>{item.quantity}</StyledTableCell>
                    <StyledTableCell></StyledTableCell>
                    <StyledTableCell>{item.release_date}</StyledTableCell>
                    <StyledTableCell>{item.state || '-'}</StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default ProductionDocument; 